import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  VictoryAxis,
  VictoryCandlestick,
  VictoryChart,
  VictoryCursorContainer,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import './index.css';
import {
  CallbackArgs,
  DomainTuple,
  LineSegment,
  Point,
  VictoryLabel,
} from 'victory-core';

import moment from 'moment';
import LoadingIcons from 'react-loading-icons';
import {
  BotTimeFrames,
  CandleType,
  CbrtRange,
} from '../../../screens/Detail/types';
import { formatPrice } from '../../../helpers/NumberFormatter';
import { ComponentDimensions } from '../../../types/Layout';
import CurrentPrice from './CurrentPrice';
import { colors } from '../../../colors';
import axios from 'axios';
import { PriceChartApiResponse } from '../../../screens/Detail/Charts';
import { FaArrowLeft } from 'react-icons/fa';
import { Transaction } from '../../../screens/Overview/types';
import {
  createXTickValues,
  getFormatUnit,
} from '../../../helpers/bundleTransactionsToCompactForm';
import ChartZoomLevel from '../../../screens/ProfitCalculations/Content/SingleProfitCalculation/Result/Charts/ChartZoomLevel';
import { CandleSticksLatestOrder } from '../../../screens/Detail/Charts/types';
import { Exchange } from '../../../types/exchange';

import ServicePaths from '../../../bridge/utils/ServicePaths';
import { XAxisInfo } from '../../MultiChart';
import {
  Band,
  IndicatorDataPoint,
  IndicatorSingleValue,
  RSIItem,
  ZoningBand,
} from '../../../screens/ProfitCalculations/types';
import BandChart from './BandChart';
import CbrtRangeChart from './CbrtRangeChart';
import TransactionDotsChart from './TransactionDotsChart';
import MovingAverageChartLegend from './MovingAverageChartLegend';
import ChannelChartLegend from './ChannelChartLegend';
import IndicatorWrapper from '../IndicatorWrapper';
import {
  calculateLongestTickWidth,
  calculateYAxisWidth,
  calculateYTicks,
  createYAxisCandlesticksTicks,
  formatToCandlestickDataPointType,
  formatYTick,
} from '../../../helpers/Chart';
import { Bot } from 'grts-utils/types/Bot';
import { DetailCompactTransaction } from '../../../types/subscription';
import { CustomTransaction } from 'grts-utils/types/Transaction';
import { GenericTransactionInfo } from '../../../types/Transaction';
import { TimeFrame } from '../../../types/Bot';

export const isZoningBand = (item: Band | ZoningBand): item is ZoningBand =>
  item.hasOwnProperty('buyBand') && item.hasOwnProperty('sellBand');

interface Props {
  baseAsset: string;
  quoteAsset: string;
  timeFrame: TimeFrame | undefined;
  candleResponse: PriceChartApiResponse | undefined;
  candleType: CandleType;
  shouldFetch?: boolean;
  isLoading: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  dimensions?: ComponentDimensions;
  transactions?: Array<GenericTransactionInfo | CustomTransaction>;
  scale?: 'linear' | 'log';
  currentPrice?: string;
  bot?: Bot | undefined;
  cbrt?: number | undefined;
  cbrtRange?: CbrtRange | undefined;
  timeFrames?: BotTimeFrames;
  transactionsByTimestamp?: Record<string, GenericTransactionInfo[]>;
  transactionsSetsLength?: number;
  multiOrderCandles?: PriceChartApiResponse | undefined;
  getIndividualTransactions?: (
    isProfitTransaction: boolean,
    compactTransactionId: string | null,
  ) => Transaction[];
  candleSticksLatestOrder?: CandleSticksLatestOrder;
  exchange: Exchange;
  domainIndex?: number;
  domainX?: [number, number];
  setDomainXes?: React.Dispatch<
    React.SetStateAction<[number, number][] | undefined>
  >;
  height?: number | undefined;
  width?: number | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  setXAxisInfo?: React.Dispatch<React.SetStateAction<XAxisInfo | undefined>>;
  movingAverages?: Array<{
    data: IndicatorSingleValue[];
    color: string;
    label: string;
  }>;
  bollingerBand?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
  keltnerChannel?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };

  chartOptionsComponent?: ReactElement;
  handleChartPress?: () => void;
  useIndicators?: boolean;
  useParentIndicators?: boolean;
  signals?: IndicatorDataPoint[] | undefined;
}

type MovingAverage = {
  x: number;
  y: number;
};

const getCandleTypeKey = (candleType: CandleType) =>
  candleType === CandleType.Normal ? 'normal' : 'heikin';

const Chart: React.FC<Props> = (props: Props) => {
  const {
    baseAsset,
    quoteAsset,
    timeFrame,
    exchange,
    candleType,
    candleResponse,
    isLoading,
    setIsLoading,
    dimensions,
    scale,
    currentPrice,
    bot,
    transactions,
    transactionsByTimestamp,
    transactionsSetsLength,
    multiOrderCandles,
    getIndividualTransactions,
    candleSticksLatestOrder,
    handleChartPress,
    signals,
  } = props;

  const {
    width,
    height,
    xAxisInfo,
    setXAxisInfo,
    movingAverages,
    bollingerBand,
    keltnerChannel,
  } = props;

  const cbrt = bot?.complexParams?.cbrt;
  const latestOrder = bot?.runtime?.latestOrder;
  const cbrtRange = bot?.runtime?.cbrtRange;
  const timeFrames = bot?.timeFrame;

  const [temporaryCandles, setTemporaryCandles] = useState<
    PriceChartApiResponse | undefined
  >();
  const [zoomLevel, setzoomLevel] = useState(4);
  const [zoomLevelDomainX, setZoomLevelDomainX] = useState<
    [number, number] | undefined
  >();
  const [zoomLevelRange, setZoomLevelRange] = useState<number | undefined>();

  const candles = useMemo(() => {
    if (!candleResponse) {
      return [];
    }

    return candleResponse[getCandleTypeKey(candleType)];
  }, [candleResponse, candleType]);

  const data = useMemo(() => {
    if (!candles) {
      return [];
    }

    if (temporaryCandles) {
      return temporaryCandles[getCandleTypeKey(candleType)].map((candle) =>
        formatToCandlestickDataPointType(candle),
      );
    }

    const dataPoints = candles.map((candle) =>
      formatToCandlestickDataPointType(candle),
    );

    return dataPoints;
  }, [candleType, candles, temporaryCandles]);

  const cbrtOpen = useMemo(() => {
    if (cbrtRange?.open) {
      return cbrtRange.open;
    }

    /**
     * We'll start at the end
     * We're looking for the start of new trend
     * For the open of the wave
     */
    if (!candleResponse?.heikin || candleResponse.heikin.length === 0) {
      return null;
    }

    let open;

    for (let i = candleResponse.heikin.length - 1; i >= 0; i--) {
      const candle = candleResponse.heikin[i];

      if (latestOrder === 'SELL' && candle.direction === 'down') {
        open = (candle.open + candle.close) / 2;
        break;
      }

      if (latestOrder === 'BUY' && candle.direction === 'up') {
        open = (candle.open + candle.close) / 2;
        break;
      }

      if (candle.startOfNewTrend) {
        open = candle.open;
        break;
      }
    }

    return open;
  }, [cbrtRange?.open, candleResponse?.heikin, latestOrder]);

  const cbrtClose = useMemo(() => {
    if (!cbrt || !cbrtOpen || !candleResponse?.heikin) {
      return null;
    }

    const cbrtDirection = cbrtRange?.direction
      ? cbrtRange.direction === 'up'
        ? 1
        : -1
      : latestOrder
      ? latestOrder === 'BUY'
        ? -1
        : 1
      : candleResponse.heikin[candleResponse.heikin.length - 1].direction ===
        'down'
      ? -1
      : 1;

    return cbrtDirection * cbrt * cbrtOpen + cbrtOpen;
  }, [
    candleResponse?.heikin,
    cbrt,
    cbrtOpen,
    cbrtRange?.direction,
    latestOrder,
  ]);

  const xStartOfCbrtLine = useMemo(() => {
    if (cbrtRange?.timestamp) {
      return cbrtRange?.timestamp;
    }

    if (!data || data.length === 0 || !cbrtClose) {
      return null;
    }

    let timestamp;

    for (let i = data.length - 1; i > 0; i--) {
      const dataPoint = data[i];

      if (dataPoint.high >= cbrtClose && cbrtClose >= dataPoint.low) {
        timestamp = dataPoint.x;
        break;
      }
    }

    return timestamp;
  }, [cbrtRange?.timestamp, cbrtClose, data]);

  const isCbrtLineStartingFromThEnd = useMemo(() => {
    if (!data || data.length === 0) {
      return false;
    }

    return xStartOfCbrtLine === data[data.length - 1].x;
  }, [data, xStartOfCbrtLine]);

  const domain = useMemo(() => {
    if (!data?.length) {
      return undefined;
    }

    const xDomainMin = data[0].x;
    const xDomainMax = data[data.length - 1].x;
    const diff = xDomainMax - xDomainMin;

    const extraRange = cbrt
      ? diff * (0.075 + (isCbrtLineStartingFromThEnd ? 0.06 : 0))
      : 0;

    let minY = Math.min(...data.map((c) => c.low));
    let maxY = Math.max(...data.map((c) => c.high));

    if (bollingerBand?.data && scale !== 'log') {
      if (isZoningBand(bollingerBand.data)) {
        minY = Math.min(
          minY,
          ...bollingerBand.data.buyBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
        maxY = Math.max(
          maxY,
          ...bollingerBand.data.sellBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
      } else {
        minY = Math.min(
          minY,
          ...bollingerBand.data.lowerBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
        maxY = Math.max(
          maxY,
          ...bollingerBand.data.upperBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
      }
    }

    if (keltnerChannel?.data && scale !== 'log') {
      if (isZoningBand(keltnerChannel.data)) {
        minY = Math.min(
          minY,
          ...keltnerChannel.data.buyBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
        maxY = Math.max(
          maxY,
          ...keltnerChannel.data.sellBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
      } else {
        minY = Math.min(
          minY,
          ...keltnerChannel.data.lowerBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
        maxY = Math.max(
          maxY,
          ...keltnerChannel.data.upperBand
            .filter((b) => b.timestamp >= xDomainMin)
            .map((b) => b.value),
        );
      }
    }

    return {
      x: (xAxisInfo?.domain.x ?? [xDomainMin, xDomainMax]) as DomainTuple,
      y: [
        Math.max(0, minY * (temporaryCandles ? 0.98 : 0.98)),
        maxY * (temporaryCandles ? 1.03 : 1.03),
      ] as DomainTuple,
    };
  }, [
    bollingerBand?.data,
    cbrt,
    data,
    xAxisInfo?.domain.x,
    isCbrtLineStartingFromThEnd,
    keltnerChannel?.data,
    scale,
    temporaryCandles,
  ]);

  const tickValues = useMemo(() => {
    if (!domain?.x) {
      return [];
    }

    return createXTickValues(domain.x[0] as number, domain.x[1] as number);
  }, [domain?.x]);

  const movingAveragesDataSets = useMemo(() => {
    if (!movingAverages?.length) {
      return [];
    }
    const dataSets: Array<{ data: MovingAverage[]; color: string }> = [];

    movingAverages.forEach((movingAverage) => {
      const range = data[0].x2 - data[0].x;

      dataSets.push({
        data: movingAverage.data.map((ma) => ({
          x: ma.timestamp - range,
          y: ma.value,
        })),
        color: movingAverage.color,
      });
    });

    return dataSets;
  }, [data, movingAverages]);

  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: setXAxisInfo ? {} : { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 13, padding: 5, fill: 'gray' },
    }),
    [setXAxisInfo],
  );

  const yTicks = calculateYTicks(data);
  const longestTickWidth = calculateLongestTickWidth(yTicks, formatYTick);
  const yAxisWidth = calculateYAxisWidth(longestTickWidth);

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: {
        fontSize: 16,
        padding: (yAxisWidth - longestTickWidth) / 2,
        fill: '#999',
      },
    }),
    [longestTickWidth, yAxisWidth],
  );

  const getCandleStrokeColor = useCallback(
    (c: any) => {
      if (
        !candleSticksLatestOrder ||
        c.datum.x <= candleSticksLatestOrder.timestamp
      ) {
        return c.datum.close >= c.datum.open ? 'green' : 'red';
      }

      if (candleSticksLatestOrder.orderType === 'BUY') {
        return c.datum.close >= c.datum.open ? 'green' : 'orange';
      }

      return c.datum.close >= c.datum.open ? 'purple' : 'red';
    },
    [candleSticksLatestOrder],
  );

  const getClosestPrice = useCallback(
    (date: string) => {
      const t = data.reduce<{
        closestPrice: number | null;
        closest: number | null;
      }>(
        (closest, current: any) => {
          if (!closest.closest) {
            return {
              closestPrice: current.close,
              closest: Math.abs(new Date(date).valueOf() - current.x),
            };
          }

          if (
            Math.abs(new Date(date).valueOf() - current.x) <= closest.closest
          ) {
            return {
              closestPrice: current.close,
              closest: Math.abs(new Date(date).valueOf() - current.x),
            };
          }

          return closest;
        },
        { closestPrice: null, closest: null },
      );

      if (!t.closestPrice) {
        return undefined;
      }

      return formatPrice(t.closestPrice, true);
    },
    [data],
  );

  const handleZoomIn = useCallback(async () => {
    /** Switch view to shorter/multiT candles */

    /** Don't handle the press a second time */
    if (temporaryCandles) {
      return;
    }

    /**
     * Find the startTimestamp
     * find the startDate of the higherT candle
     * Of the start of the previous wave
     */
    let startTimestamp;
    let startOfCurrentWaveFound = false;

    if (!candleResponse?.heikin?.length) {
      return;
    }

    for (let i = candleResponse.heikin.length - 1; i > 0; i--) {
      const candle = candleResponse.heikin[i];

      if (candle.startOfNewTrend) {
        if (!startOfCurrentWaveFound) {
          startOfCurrentWaveFound = true;
          continue;
        } else {
          startTimestamp = candle.startTimestamp;
          break;
        }
      }
    }

    if (!startTimestamp) {
      return;
    }

    const timeFrame = timeFrames?.multiOrder;

    startTimestamp =
      new Date().valueOf() - (new Date().valueOf() - startTimestamp) / 3;

    /** Now we can fetch the candles for this timeFrame
     * Put them in a state
     * and display them
     */
    const { data }: { data: PriceChartApiResponse } = await axios({
      method: 'GET',
      url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
      params: {
        baseAsset,
        quoteAsset,
        timeFrame,
        startTimestamp,
        endTimestamp: new Date().valueOf(),
        includeLatestCandle: true,
        exchange,
      },
    });

    setTemporaryCandles(data);
  }, [
    baseAsset,
    candleResponse?.heikin,
    exchange,
    quoteAsset,
    temporaryCandles,
    timeFrames?.multiOrder,
  ]);

  const getZoomLevelTimeFrame = useCallback(
    (zoomLevel: number) => {
      switch (zoomLevel) {
        case 4:
        case 3:
          return bot?.timeFrame?.multiOrder;

        case 2:
          return bot?.timeFrame?.shorter;

        default:
          return bot?.timeFrame?.higher;
      }
    },
    [
      bot?.timeFrame?.higher,
      bot?.timeFrame?.multiOrder,
      bot?.timeFrame?.shorter,
    ],
  );

  // const handleTransactionPress = useCallback(
  //   async (
  //     event: React.SyntheticEvent<any, Event>,
  //     eventProps: { index: number; data: Datum[]; datum: Datum },
  //   ) => {
  //     if (
  //       !bot ||
  //       !transactions ||
  //       !transactionsByTimestamp ||
  //       temporaryCandles
  //     ) {
  //       return;
  //     }

  //     if (isCustomTransaction(eventProps.datum.t)) {
  //       return;
  //     }

  //     setIsLoading?.(true);

  //     /**
  //      * This function will fetch candles for the zoomedIn state of the transactions
  //      *
  //      * Get the transaction timestamp
  //      * Find the respective transaction
  //      * (I think it's best to pair them together already before this function?)
  //      *
  //      * Fetch the candles in between them + extra range
  //      */

  //     const isProfitCalcs = eventProps.datum.isProfitCalcs;
  //     const realTransactions = transactions[0];
  //     const profitCalcsTransactions = transactions[1];
  //     const otherTransactionSet = isProfitCalcs
  //       ? realTransactions
  //       : profitCalcsTransactions;

  //     const currentTransactionSet = isProfitCalcs
  //       ? profitCalcsTransactions
  //       : realTransactions;

  //     const getEndTimestamp = (
  //       transaction: Transaction,
  //       startTimestamp: number,
  //     ) => {
  //       const transactionIndex = currentTransactionSet
  //         .sort((a, b) => a.transactionTimestamp - b.transactionTimestamp)
  //         .findIndex(
  //           (t) => t.transactionTimestamp === transaction.transactionTimestamp,
  //         );

  //       if (
  //         transactionIndex !== -1 &&
  //         transactionIndex < currentTransactionSet.length - 1
  //       ) {
  //         const nextTransaction = currentTransactionSet[transactionIndex + 1];

  //         return nextTransaction.transactionTimestamp;
  //       }

  //       return (
  //         transaction.transactionTimestamp +
  //         (transaction.transactionTimestamp - startTimestamp) * 0.4
  //       );
  //     };

  //     let startTimestamp, endTimestamp;

  //     if (transactionsSetsLength === 1) {
  //       /** Start of the wave until next transaction or end */
  //       startTimestamp = calculateStartOfWaveTimestamp(eventProps.datum.x);
  //       endTimestamp = getEndTimestamp(eventProps.datum.t, startTimestamp);
  //     } else {
  //       const transactionTimestamp =
  //         eventProps.datum.t.transactionTimestamp +
  //         (isProfitCalcs
  //           ? getTimeFrameIntervalTimestamp(
  //               bot?.timeFrame.multiOrder || TimeFrame.OneWeek,
  //             )
  //           : 0);

  //       const x = findMultiOrderCandleStartTimestamp(transactionTimestamp);
  //       const linkedTransactions = transactionsByTimestamp[x];

  //       /**
  //        * if undefined, no handle
  //        * if length 1, we haave to find the other trasnsaction (same orderType)
  //        * if length is 2, they are correctly synced
  //        */

  //       if (typeof linkedTransactions === 'undefined') {
  //         setIsLoading?.(false);
  //         return;
  //       }

  //       const transaction = linkedTransactions[0];

  //       if (linkedTransactions.length === 1) {
  //         /** if length 1, we have to find the other transaction (same orderType)
  //          * Find the transaction with the smallest abs diff with our
  //          * current transaction timestamp and same orderType
  //          *
  //          * If there is no other transaction set,
  //          * just zoom in on the transaction timestamp
  //          */

  //         if (!profitCalcsTransactions?.length) {
  //           /** Zoom in on the current transaction
  //            * Get the start of the current wave
  //            * End is next transaction or *now*
  //            */
  //           const startOfWaveTimestamp =
  //             calculateStartOfWaveTimestamp(transactionTimestamp);

  //           startTimestamp = startOfWaveTimestamp;
  //           endTimestamp = getEndTimestamp(transaction, startTimestamp);
  //         } else {
  //           /**
  //            * Find the transaction with the smallest abs diff with our
  //            * current transaction timestamp and same orderType
  //            */
  //           const closestTransaction = otherTransactionSet.reduce(
  //             (acc, curr) => {
  //               if (acc === null) {
  //                 return transaction.orderType !== curr.orderType ? acc : curr;
  //               }

  //               if (
  //                 Math.abs(curr.transactionTimestamp - transactionTimestamp) <=
  //                   Math.abs(acc.transactionTimestamp - transactionTimestamp) &&
  //                 transaction.orderType === curr.orderType
  //               ) {
  //                 return curr;
  //               }

  //               return acc;
  //             },
  //             null as Transaction | null,
  //           );

  //           if (!closestTransaction) {
  //             /** Show start of wave until next transaction or *now* */
  //             const startOfWaveTimestamp =
  //               calculateStartOfWaveTimestamp(transactionTimestamp);

  //             startTimestamp = startOfWaveTimestamp;
  //             endTimestamp = getEndTimestamp(transaction, startTimestamp);
  //           } else {
  //             /** Show from min timestamp until max timestamp + range */
  //             startTimestamp = Math.min(
  //               Math.min(
  //                 transaction.transactionTimestamp,
  //                 closestTransaction.transactionTimestamp,
  //               ),
  //               calculateStartOfWaveTimestamp(
  //                 Math.min(
  //                   closestTransaction.transactionTimestamp,
  //                   transaction.transactionTimestamp,
  //                 ),
  //               ),
  //             );
  //             endTimestamp =
  //               Math.max(
  //                 transaction.transactionTimestamp,
  //                 closestTransaction.transactionTimestamp,
  //               ) * 1.00002;
  //           }
  //         }
  //       }

  //       if (linkedTransactions.length === 2) {
  //         /**
  //          * @TODO
  //          * if buying, start at the start of the green higherT wave
  //          * end right after the transactions / or right up until the next transaction?
  //          */
  //         /**
  //          * Get transaction timestamp
  //          * get the candle from data
  //          * search back for the start of the wave
  //          * now we have the startTimestamp
  //          *
  //          * 1.
  //          * Now search for the transaction index
  //          * take the timestamp of the next transaction
  //          *
  //          * 2.
  //          * Or just take an extra 2 candles afterwards
  //          */
  //         /** Show start of wave until next transaction or *now* */
  //         const startOfWaveTimestamp =
  //           calculateStartOfWaveTimestamp(transactionTimestamp);

  //         startTimestamp = startOfWaveTimestamp;
  //         endTimestamp = getEndTimestamp(eventProps.datum.t, startTimestamp);
  //       }
  //     }

  //     /** Fetch the multiOrderCandles from start to end */
  //     if (!startTimestamp || !endTimestamp || !bot) {
  //       setIsLoading?.(false);
  //       return;
  //     }

  //     startTimestamp = Math.floor(
  //       startTimestamp - (endTimestamp - startTimestamp) * 0.05,
  //     );
  //     endTimestamp = Math.ceil(
  //       endTimestamp + (endTimestamp - startTimestamp) * 0.05,
  //     );

  //     const range =
  //       (eventProps.datum.compactTransactionTimestamp ?? endTimestamp) +
  //       ((eventProps.datum.compactTransactionTimestamp ?? endTimestamp) -
  //         startTimestamp) *
  //         0.3;

  //     const { data } = await axios({
  //       url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
  //       params: {
  //         baseAsset: bot.baseAsset,
  //         quoteAsset: bot.quoteAsset,
  //         timeFrame: getZoomLevelTimeFrame(zoomLevel),
  //         startTimestamp,
  //         endTimestamp: zoomLevel === 4 ? range : endTimestamp,
  //         includeLatestCandle: true,
  //         exchange: bot.defaultExchange,
  //       },
  //     });

  //     setTemporaryCandles(data);
  //     setZoomLevelDomainX([startTimestamp, endTimestamp]);
  //     setZoomLevelRange(range);
  //     setIsLoading?.(false);
  //   },
  //   [
  //     bot,
  //     calculateStartOfWaveTimestamp,
  //     findMultiOrderCandleStartTimestamp,
  //     getZoomLevelTimeFrame,
  //     setIsLoading,
  //     temporaryCandles,
  //     transactions,
  //     transactionsByTimestamp,
  //     transactionsSetsLength,
  //     zoomLevel,
  //   ],
  // );

  const handleBackPress = useCallback(() => {
    setTemporaryCandles(undefined);
    setZoomLevelDomainX(undefined);
  }, []);

  const handleLowerZoomLevel = useCallback(async () => {
    if (zoomLevel === 1 || !temporaryCandles || !bot || !zoomLevelDomainX) {
      return;
    }

    setIsLoading?.(true);

    const { data } = await axios({
      url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
      params: {
        baseAsset: bot.baseAsset,
        quoteAsset: bot.quoteAsset,
        timeFrame: getZoomLevelTimeFrame(zoomLevel - 1),
        startTimestamp: zoomLevelDomainX[0],
        endTimestamp: zoomLevelDomainX[1],
        includeLatestCandle: true,
        exchange,
      },
    });

    setTemporaryCandles(data);
    setzoomLevel((prev) => prev - 1);
    setIsLoading?.(false);
  }, [
    bot,
    exchange,
    getZoomLevelTimeFrame,
    setIsLoading,
    temporaryCandles,
    zoomLevel,
    zoomLevelDomainX,
  ]);

  const handleRaiseZoomLevel = useCallback(async () => {
    if (zoomLevel === 4 || !temporaryCandles || !bot || !zoomLevelDomainX) {
      return;
    }

    setIsLoading?.(true);

    const { data } = await axios({
      url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
      params: {
        baseAsset: bot.baseAsset,
        quoteAsset: bot.quoteAsset,
        timeFrame: getZoomLevelTimeFrame(zoomLevel + 1),
        startTimestamp: zoomLevelDomainX[0],
        endTimestamp: zoomLevel === 3 ? zoomLevelRange : zoomLevelDomainX[1],
        includeLatestCandle: true,
        exchange,
      },
    });

    setTemporaryCandles(data);
    setzoomLevel((prev) => prev + 1);
    setIsLoading?.(false);
  }, [
    bot,
    exchange,
    getZoomLevelTimeFrame,
    setIsLoading,
    temporaryCandles,
    zoomLevel,
    zoomLevelDomainX,
    zoomLevelRange,
  ]);

  const showBotState = !!cbrt;

  const yTickValues = useMemo(() => {
    if (!domain) {
      return [];
    }

    return createYAxisCandlesticksTicks(
      data,
      [],
      bollingerBand?.data || keltnerChannel?.data,
      domain,
    );
  }, [bollingerBand?.data, data, domain, keltnerChannel?.data]);

  const domainPadding = useMemo(
    () => ({ x: showBotState ? 10 : 10 }),
    [showBotState],
  );

  const paddingRight = useMemo(() => {
    if (!yTickValues?.length) {
      return 0;
    }

    const longestTick = yTickValues
      .sort((a, b) => b.toString().length - a.toString().length)[0]
      .toString();

    const length = longestTick.length;
    const hasDecimal = longestTick.includes(',') || longestTick.includes('.');
    return 20 + length * 10 - (hasDecimal ? 5 : 0);
  }, [yTickValues]);

  const chartPadding = useMemo(
    () => ({
      left: 0,
      right: yAxisWidth,
      bottom: setXAxisInfo ? 0 : 30,
      top: 5,
    }),
    [setXAxisInfo, yAxisWidth],
  );

  useEffect(() => {
    if (!domain?.x || !tickValues?.length) {
      return;
    }

    setXAxisInfo?.({
      tickValues,
      axisStyle,
      tickFormat: calculateTickValue,
      domain: { x: domain.x as [number, number] },
      domainPadding,
      chartPadding,
    });
  }, [
    axisStyle,
    calculateTickValue,
    chartPadding,
    domain?.x,
    domainPadding,
    setXAxisInfo,
    tickValues,
  ]);

  const chartWidth = width || dimensions?.width;
  const chartHeight = height || dimensions?.height;

  const rootClassName = useMemo(() => {
    const className = 'candlesticks-container';

    if (!!handleChartPress) {
      return `${className} candlesticks-press`;
    }

    return className;
  }, [handleChartPress]);

  const signalDataPoints =
    signals?.map((s) => ({
      x: s.timestamp,
      y: s.price,
      color: s.signalType === 'BUY' ? colors.green : colors.red,
    })) || [];

  return (
    <div
      className={rootClassName}
      style={{
        width: chartWidth,
        height: (chartHeight ?? 0) * 0.8,
        opacity: dimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
      }}
      onClick={handleChartPress}
    >
      <VictoryChart
        key={`candlesticks_${timeFrame}`}
        scale={{ x: 'time', y: scale || 'linear' }}
        domainPadding={domainPadding}
        domain={domain}
        width={chartWidth ?? 0}
        height={(chartHeight ?? 0) * 0.8}
        padding={chartPadding}
        containerComponent={
          <VictoryCursorContainer
            cursorDimension="x"
            cursorLabel={({ datum }: CallbackArgs) =>
              `${new Date(datum.x).toUTCString()}\n${getClosestPrice(datum.x)}`
            }
            cursorComponent={
              <LineSegment style={{ opacity: 0.3, stroke: '#999' }} />
            }
            cursorLabelComponent={
              <VictoryLabel
                style={{ fill: '#999' }}
                y={20}
                textAnchor={(e: any) => {
                  if (e.x <= 1150) {
                    return 'start';
                  }

                  return 'end';
                }}
              />
            }
          />
        }
      >
        <VictoryAxis
          tickFormat={!setXAxisInfo ? calculateTickValue : () => ''}
          style={axisStyle}
          tickValues={tickValues}
          tickCount={12}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={formatYTick}
          orientation={'right'}
          tickValues={yTickValues}
          width={yAxisWidth}
        />

        <BandChart band={bollingerBand?.data} domain={domain} />
        <BandChart band={keltnerChannel?.data} domain={domain} />

        {movingAveragesDataSets &&
          movingAveragesDataSets.map(({ data, color }, i) => (
            <VictoryLine
              key={`moving_average_${i}`}
              data={data}
              style={{
                data: {
                  stroke: color,
                  strokeWidth: 1,
                  opacity: 0.75,
                },
              }}
            />
          ))}

        <CbrtRangeChart
          data={data}
          domain={domain}
          candleResponse={candleResponse}
          bot={bot}
          handleZoomIn={handleZoomIn}
          temporaryCandles={temporaryCandles}
        />

        <VictoryCandlestick
          candleColors={{ positive: 'green', negative: 'red' }}
          data={data}
          style={{
            data: {
              fill: getCandleStrokeColor,
              stroke: getCandleStrokeColor,
              strokeWidth: 0.5,
            },
          }}
          closeLabelComponent={
            <CurrentPrice
              length={data.length}
              currentPrice={currentPrice}
              handleZoomIn={handleZoomIn}
              chartWidth={chartWidth}
              paddingRight={paddingRight}
            />
          }
          closeLabels={!!currentPrice}
        />

        <TransactionDotsChart
          baseAsset={baseAsset}
          quoteAsset={quoteAsset}
          data={data}
          candleResponse={candleResponse}
          temporaryCandles={temporaryCandles}
          setTemporaryCandles={setTemporaryCandles}
          domain={domain}
          setIsLoading={setIsLoading}
          bot={bot}
          transactions={transactions}
          transactionsByTimestamp={transactionsByTimestamp}
          transactionsSetsLength={transactionsSetsLength}
          multiOrderCandles={multiOrderCandles}
          // getIndividualTransactions={getIndividualTransactions}
          zoomLevel={zoomLevel}
          setZoomLevelDomainX={setZoomLevelDomainX}
          setZoomLevelRange={setZoomLevelRange}
        />

        {!!signalDataPoints?.length && (
          <VictoryScatter
            data={signalDataPoints}
            style={{
              data: {
                fill: ({ datum }) => datum.color,
                stroke: colors.white,
                strokeWidth: 1,
              },
            }}
            dataComponent={
              <Point
                className={
                  !temporaryCandles
                    ? 'single-profit-calcs-chart-scatter-dot'
                    : undefined
                }
              />
            }
            size={3}
          />
        )}
      </VictoryChart>
      {isLoading && (
        <div className="candlesticks-loading-container">
          <LoadingIcons.Grid width={40} />
        </div>
      )}
      {!!temporaryCandles && (
        <div
          className="candlesticks-chart-back-container"
          onClick={handleBackPress}
        >
          <FaArrowLeft color={colors.white} size={24} />
        </div>
      )}
      {!!temporaryCandles && (
        <ChartZoomLevel
          isVisible={!!temporaryCandles}
          zoomLevel={zoomLevel}
          handleZoomIn={handleLowerZoomLevel}
          handleZoomOut={handleRaiseZoomLevel}
        />
      )}
      {!!movingAverages && (
        <MovingAverageChartLegend
          movingAverages={movingAverages}
          domain={domain}
          candles={data}
        />
      )}
      {(!!bollingerBand || !!keltnerChannel) && (
        <ChannelChartLegend
          channel={bollingerBand || keltnerChannel}
          label={!!bollingerBand ? 'Bollinger Band' : 'Keltner Channel'}
          domain={domain}
          candles={data}
          skip={!!movingAverages}
        />
      )}
    </div>
  );
};

const CandleSticksChart = (props: Props) => {
  /** Chart at the top
   * Legend on the bottom
   */

  const {
    candleType,
    candleResponse,
    chartOptionsComponent,
    dimensions,
    baseAsset,
    quoteAsset,
    timeFrame,
    exchange,
    shouldFetch,
    useIndicators = true,
    useParentIndicators = false,
  } = props;

  const currentCandles = useMemo(
    () =>
      candleType === CandleType.Normal
        ? candleResponse?.normal
        : candleResponse?.heikin,
    [candleType, candleResponse?.heikin, candleResponse?.normal],
  );

  return (
    // <IndicatorWrapper
    //   dimensions={dimensions}
    //   candles={currentCandles}
    //   baseAsset={baseAsset}
    //   quoteAsset={quoteAsset}
    //   timeFrame={timeFrame}
    //   shouldFetch={shouldFetch}
    //   useIndicators={useIndicators}
    //   useParentIndicators={useParentIndicators}
    //   useTimeFrameSpecificConfig={false}
    //   exchange={exchange}
    // >
    <Chart {...props} />
    //   {chartOptionsComponent}
    // </IndicatorWrapper>
  );
};

export default React.memo(CandleSticksChart);
